import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { AddIcon, ArrowBackIcon, ArrowForwardIcon } from 'utils/icons';
import Chip from 'components/Chip';
import API_ENDPOINTS from 'utils/api/endpoints';

const ContextualSuggestions = ({
  contextualSuggestions,
  addedContextualSuggestions,
  swimlaneItems,
  scrollPosition,
  onBackClick,
  onForwardClick,
  onSuggestionClick,
  hasEditAccess,
  contextualSuggestionsRef,
  budgetId,
  categoryId,
  isDetailView,
  suggestionScopeKey,
  categoryColumnFingerprint,
  hiddenSuggestionKeys,
}) => {
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const aiSuggestionsCache = useRef(new Map());
  const activeCategoryColumnRef = useRef(null);
  const categoryIdRef = useRef(categoryId);
  const requestContextKey = useMemo(() => {
    const hasTemporaryCategoryId = /^NEW_ITEM_/.test(String(categoryId || ''));
    if (!budgetId || hasTemporaryCategoryId) return null;
    if (isDetailView) {
      return `detail:${budgetId}:${categoryId || ''}`;
    }
    return `category:${budgetId}:${suggestionScopeKey}:${
      categoryColumnFingerprint || 'pending'
    }`;
  }, [
    budgetId,
    categoryColumnFingerprint,
    categoryId,
    isDetailView,
    suggestionScopeKey,
  ]);

  useEffect(() => {
    categoryIdRef.current = categoryId;
  }, [categoryId]);

  useEffect(() => {
    if (!requestContextKey) {
      setAiSuggestions(null);
      return undefined;
    }

    const columnIdentity = `${budgetId}:${suggestionScopeKey}`;
    const activeColumn = activeCategoryColumnRef.current;
    if (
      !isDetailView &&
      activeColumn?.identity === columnIdentity &&
      activeColumn?.fingerprint !== categoryColumnFingerprint &&
      activeColumn?.loaded
    ) {
      // Keep the current column's suggestions stable after an add/delete. The
      // next visit to this column will use its new fingerprint and refresh.
      return undefined;
    }

    if (!isDetailView) {
      activeCategoryColumnRef.current = {
        identity: columnIdentity,
        fingerprint: categoryColumnFingerprint,
        loaded: false,
      };
    } else {
      activeCategoryColumnRef.current = null;
    }

    if (aiSuggestionsCache.current.has(requestContextKey)) {
      setAiSuggestions(aiSuggestionsCache.current.get(requestContextKey));
      if (!isDetailView) {
        activeCategoryColumnRef.current = {
          identity: columnIdentity,
          fingerprint: categoryColumnFingerprint,
          loaded: true,
        };
      }
      return undefined;
    }

    setAiSuggestions([]);
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const response = await API_ENDPOINTS.ai_suggestions.contextual({
          budget_id: budgetId,
          category_id: categoryIdRef.current || undefined,
          context_mode: isDetailView ? 'detail' : 'category',
        });
        const suggestions = response?.data?.suggestions || [];
        aiSuggestionsCache.current.set(requestContextKey, suggestions);
        if (active) {
          setAiSuggestions(suggestions);
          if (!isDetailView) {
            activeCategoryColumnRef.current = {
              identity: columnIdentity,
              fingerprint: categoryColumnFingerprint,
              loaded: true,
            };
          }
        }
      } catch (_) {
        // The static suggestions remain available until AI is configured.
        if (active) setAiSuggestions(null);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    budgetId,
    categoryColumnFingerprint,
    isDetailView,
    requestContextKey,
    suggestionScopeKey,
  ]);

  const suggestionSource =
    aiSuggestions === null ? contextualSuggestions : aiSuggestions;
  const suggestions = suggestionSource?.filter(suggestionItem => {
    const suggestionName = suggestionItem?.label || suggestionItem?.name;
    const suggestionKey = `${suggestionScopeKey}:${String(suggestionName || '')
      .trim()
      .toLowerCase()}`;
    return !(
      hiddenSuggestionKeys[suggestionKey] ||
      addedContextualSuggestions[suggestionName] ||
      swimlaneItems?.some(
        item => item?.name?.toLowerCase() === suggestionName?.toLowerCase()
      )
    );
  });

  return (
    <div className="contextual-suggestions">
      <div className="contextual-suggestions__title">Suggestions:</div>
      <div className="contextual-suggestions__container">
        <ArrowBackIcon
          className={`back-icon ${scrollPosition > 0 ? '' : 'disabled'}`}
          onClick={onBackClick}
        />
        <div
          className="contextual-suggestions__container__chip-wrapper"
          ref={contextualSuggestionsRef}
        >
          {suggestions?.map(item => (
            <Chip
              key={item?.id || item?.label || item?.name}
              className="suggestions-chip"
              title={item?.label || item?.name}
              icon={<AddIcon />}
              onClick={onSuggestionClick({
                ...item,
                name: item?.label || item?.name,
              })}
              disabled={!hasEditAccess()}
              fringe=""
            />
          ))}
        </div>
        <ArrowForwardIcon
          className={`forward-icon ${
            scrollPosition < contextualSuggestionsRef?.current?.scrollWidth
              ? ''
              : 'disabled'
          }`}
          onClick={onForwardClick}
        />
      </div>
    </div>
  );
};

ContextualSuggestions.propTypes = {
  contextualSuggestions: PropTypes.arrayOf(PropTypes.object),
  addedContextualSuggestions: PropTypes.object,
  swimlaneItems: PropTypes.arrayOf(PropTypes.object),
  scrollPosition: PropTypes.number.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onForwardClick: PropTypes.func.isRequired,
  onSuggestionClick: PropTypes.func.isRequired,
  hasEditAccess: PropTypes.func.isRequired,
  contextualSuggestionsRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isDetailView: PropTypes.bool,
  suggestionScopeKey: PropTypes.string,
  categoryColumnFingerprint: PropTypes.string,
  hiddenSuggestionKeys: PropTypes.object,
};

ContextualSuggestions.defaultProps = {
  contextualSuggestions: [],
  addedContextualSuggestions: {},
  swimlaneItems: [],
  contextualSuggestionsRef: null,
  budgetId: null,
  categoryId: null,
  isDetailView: false,
  suggestionScopeKey: 'category:root',
  categoryColumnFingerprint: undefined,
  hiddenSuggestionKeys: {},
};

export default ContextualSuggestions;
